import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import clsx from 'clsx'
import Avatar from 'react-avatar'
import PropTypes from 'prop-types'

import Btn from '../../../../../UI/Btn'
import Checkbox from '../../../../../UI/Checkbox'
import TextAreaField from '../../../../../UI/TextAreaField'
import Loading from '../../../../../UI/Loading'
import http from '../../../../../shared/http'
import { TApi } from '../../../../../shared/const'
import { useAvatarUrls } from '../../../../../shared/useAvatarUrls'

import styles from '../style.module.scss'
import { formatVienna } from '../../../../../shared/datetime'

const AUDIENCE = { STUDENTS: 'STUDENTS', TEACHERS: 'TEACHERS' }

// Was in der Zeitleiste steht, wenn ein Ereignis automatisch mitgeschrieben
// wurde. Der Schluessel kommt aus services/companyLog im Backend.
export const EVENT_LABELS = {
	COMPANY_SUBMITTED: 'Company submitted for review',
	COMPANY_ACTIVATED: 'Company activated',
	COMPANY_REJECTED: 'Company rejected',
	COMPANY_CERTIFICATE_CREATED: 'Company certificate created',
	COMPANY_CERTIFICATE_REGENERATED: 'Company certificate regenerated',
	BUSINESS_PLAN_UPLOADED: 'Business plan uploaded',
	FINAL_REPORT_UPLOADED: 'Final report uploaded',
	SEMIANNUAL_REPORT_SUBMITTED: 'Semiannual report submitted',
	ANNUAL_REPORT_SUBMITTED: 'Annual report submitted',
	SEMIANNUAL_REPORT_UNSUBMITTED: 'Semiannual report returned',
	ANNUAL_REPORT_UNSUBMITTED: 'Annual report returned',
	COMPANY_NAME_CHANGED: 'Company name changed',
	COMPANY_EMAIL_CHANGED: 'Company email changed',
	// Nachweise, die eine Lehrkraft an Stelle einer Schueler:in hochgeladen hat
	// (Anforderung #94). Der Satz daneben nennt die Person.
	PARENTAL_CONFIRMATION_UPLOADED: 'Parental confirmation uploaded by the teacher',
	COURSE_CERTIFICATE_UPLOADED: 'Course certificate uploaded by the teacher',
	// Beanstandet: Die Datei liegt noch da. Entfernt: Sie ist geloescht. Der
	// Verlauf muss das auseinanderhalten — sonst sucht jemand eine Datei, die es
	// nicht mehr gibt.
	PARENTAL_CONFIRMATION_OBJECTED: 'Parental confirmation objected to by the teacher',
	COURSE_CERTIFICATE_OBJECTED: 'Course certificate objected to by the teacher',
	PARENTAL_CONFIRMATION_REMOVED: 'Parental confirmation removed by the teacher',
	COURSE_CERTIFICATE_REMOVED: 'Course certificate removed by the teacher',
	HYGIENE_CONFIRMED: 'Food hygiene confirmation submitted',
	HYGIENE_OBJECTED: 'Objection to the food hygiene confirmation',
	// Skills4Retail-Konzept. „Waived" ist die Freigabe der Administration im
	// Stoerfall: Das Konzept bleibt unbewertet, haelt die Company aber nicht
	// mehr auf.
	CONCEPT_SUBMITTED: 'Concept submitted for assessment',
	CONCEPT_RETURNED: 'Concept returned for revision',
	CONCEPT_SCORED: 'Concept fully assessed',
	CONCEPT_EXEMPTED: 'Exempted from the concept quizzes',
	CONCEPT_EXEMPTION_REVOKED: 'Quiz exemption revoked',
	CONCEPT_WAIVED: 'Concept requirement waived by the administration',
	// Die Verwaltung hat das Konto einer Schüler:in gelöscht (#226). Der Satz
	// daneben nennt den Namen — das Konto selbst gibt es danach nicht mehr.
	STUDENT_ACCOUNT_DELETED: 'Student account deleted',
	// Ebenso das Konto einer zugeordneten Lehrkraft (#239); hat die Betreuung
	// gewechselt, nennt der Satz daneben auch die Nachfolge.
	TEACHER_ACCOUNT_DELETED: 'Teacher account deleted',
	// Geschäftstätigkeit nach der Aktivierung (#230). Ändert die Verwaltung oder
	// gibt sie frei, sehen es Schüler:innen und Lehrkräfte mit (`audience`).
	BUSINESS_ACTIVITY_CHANGED: 'Business activity changed',
	RISK_ANALYSIS_CHANGED: 'Market and risk analysis changed during the release',
	REVISION_RELEASED: 'Released for revision',
	REVISION_RELEASE_REVOKED: 'Release revoked'
}

/**
 * Reiter „Log-Verlauf & Kommentare".
 *
 * Links die Zeitleiste: automatisch mitgeschriebene Ereignisse (aktiviert,
 * abgelehnt samt Grund, Firmenbuchauszug erstellt, Unterlage hochgeladen) und
 * die Kommentare der Verwaltung in einer Liste — sonst muesste man zwei Listen
 * nebeneinander lesen, um zu verstehen, was wann passiert ist.
 *
 * Darunter das Feld zum Erfassen. Ein Kommentar bleibt standardmaessig in der
 * Verwaltung; erst mit einem Haken bei Schueler:innen oder Lehrkraeften und
 * einem Weg (interne Nachricht, E-Mail) geht er hinaus.
 *
 * **Mit `readOnly` (Sicht der Lehrkraft)** bleibt nur die Zeitleiste, und sie
 * kommt aus `url`: `GET common/company-log/:id` liefert ausschliesslich die
 * Kommentare, die ausdruecklich an die Lehrkraefte gerichtet waren — die
 * internen Notizen der Verwaltung und die mitgeschriebenen Ereignisse bleiben
 * dort, wo sie hingehoeren. Erfassen kann in dieser Ansicht niemand: Der Weg
 * von der Schule an die Verwaltung ist die Anfrage (Hilfe-Knopf), nicht der
 * Verlauf des Unternehmens.
 */
export default function LogTab({ companyId, url, readOnly }) {
	const { t } = useTranslation()

	const [entries, setEntries] = useState(null)
	const [message, setMessage] = useState('')
	const [audience, setAudience] = useState([])
	const [viaNotification, setViaNotification] = useState(true)
	const [viaEmail, setViaEmail] = useState(false)
	const [isSaving, setSaving] = useState(false)

	// Zwei Antwortformen: die Verwaltung bekommt die Liste, die gemeinsame Route
	// zusaetzlich den Namen der Junior Company (den braucht die eigene Seite
	// „Nachrichten der Administration", hier nicht - der Name steht ohnehin ueber
	// dem Profil).
	const load = () =>
		http
			.get(url(companyId))
			.then(({ data }) => setEntries(Array.isArray(data) ? data : data?.entries || []))
			.catch(() => setEntries([])) // Meldung kommt aus shared/http

	useEffect(() => {
		load()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyId, url])

	/*
	 * Die Bilder der schreibenden Personen, angemeldet geholt und als lokale
	 * Adresse eingesetzt — ein `<img src>` auf die Schnittstelle schickt keinen
	 * Anmelde-Kopfeintrag mit (siehe `shared/useAvatarUrls`).
	 */
	const avatarUrls = useAvatarUrls(useMemo(() => (entries || []).map(entry => entry.authorAvatarHashName), [entries]))

	const toggleAudience = key =>
		setAudience(current => (current.includes(key) ? current.filter(item => item !== key) : [...current, key]))

	// Empfaenger:innen ohne Weg waeren eine Falle: der Eintrag stuende bei den
	// Schueler:innen bzw. Lehrkraeften im Verlauf, ohne dass sie davon erfahren.
	const missingChannel = audience.length > 0 && !viaNotification && !viaEmail
	const canSave = !!message.trim() && !missingChannel

	const submit = () => {
		const text = message.trim()
		if (!text || missingChannel) return

		setSaving(true)
		http.post(TApi.ADMIN_COMPANY_LOG(companyId), {
			message: text,
			audience,
			viaNotification: audience.length ? viaNotification : false,
			viaEmail: audience.length ? viaEmail : false
		})
			.then(() => {
				setMessage('')
				setAudience([])
				toast.success(t('Entry saved'))
				return load()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setSaving(false))
	}

	const recipientsHint = () => {
		if (!audience.length) return t('Comment stays internal')

		const who = audience.map(key => (key === AUDIENCE.STUDENTS ? t('employees') : t('teachers'))).join(' + ')
		const how = [viaNotification && t('Internal message'), viaEmail && t('Email')].filter(Boolean).join(' + ')

		return how ? `${who} — ${how}` : t('Pick at least one channel')
	}

	return (
		<div className={styles.stack}>
			{!readOnly && (
				<section className={styles.block}>
					<h3 className={styles.blockTitle}>{t('New entry')}</h3>
					<TextAreaField
						height={110}
						classNameLabel={styles.logTextArea}
						label={t('Comment on this company')}
						placeholder={t('Type comment about company')}
						value={message}
						register={{
							name: 'logComment',
							value: message,
							onChange: e => setMessage(e.target.value)
						}}
					/>
					<div className={styles.logOptions}>
						<div className={styles.logOptionGroup}>
							<span className={styles.logOptionLabel}>{t('Also send to')}</span>
							<Checkbox
								label={t('employees')}
								register={{
									checked: audience.includes(AUDIENCE.STUDENTS),
									onChange: () => toggleAudience(AUDIENCE.STUDENTS)
								}}
							/>
							<Checkbox
								label={t('teachers')}
								register={{
									checked: audience.includes(AUDIENCE.TEACHERS),
									onChange: () => toggleAudience(AUDIENCE.TEACHERS)
								}}
							/>
						</div>
						<div className={clsx(styles.logOptionGroup, !audience.length && styles.logOptionGroupOff)}>
							<span className={styles.logOptionLabel}>{t('Delivery')}</span>
							<Checkbox
								label={t('Internal message')}
								disabled={!audience.length}
								register={{
									checked: viaNotification,
									onChange: () => setViaNotification(value => !value)
								}}
							/>
							<Checkbox
								label={t('Email')}
								disabled={!audience.length}
								register={{
									checked: viaEmail,
									onChange: () => setViaEmail(value => !value)
								}}
							/>
						</div>
					</div>
					<div className={styles.logSubmitRow}>
						<span className={styles.logRecipients}>{recipientsHint()}</span>
						<Btn
							loading={isSaving}
							disabled={!canSave}
							width={214}
							type="button"
							icon="save"
							iconSize={{ w: 15, h: 'auto' }}
							iconColor="#fff"
							onClick={submit}
						>
							{t('save')}
						</Btn>
					</div>
				</section>
			)}

			<section className={styles.block}>
				<h3 className={styles.blockTitle}>{readOnly ? t('Messages from the administration') : t('History')}</h3>
				{entries === null && <Loading />}
				{entries?.length === 0 && (
					<div className={styles.emptyPanel}>{readOnly ? t('No messages yet') : t('No entries yet')}</div>
				)}
				{!!entries?.length && (
					<ol className={styles.timeline}>
						{entries.map(entry => {
							const isEvent = entry.type === 'EVENT'
							const shared = (entry.audience || []).length > 0

							return (
								<li key={entry.id} className={styles.timelineItem}>
									{/*
									 * Am Beitrag das Gesicht, am Ereignis der Punkt:
									 * Eine Nachricht hat jemand geschrieben, ein
									 * Ereignis ist bloß geschehen.
									 */}
									{isEvent ? (
										<span className={clsx(styles.timelineDot, styles.timelineDotEvent)} />
									) : (
										<Avatar
											className={styles.timelineAvatar}
											src={avatarUrls[entry.authorAvatarHashName] || undefined}
											name={entry.authorName || '?'}
											color="#e5eaf5"
											fgColor="#22404d"
											size="28"
											round
										/>
									)}
									<div className={styles.timelineBody}>
										<div className={styles.timelineHead}>
											<span className={styles.timelineTitle}>
												{isEvent
													? t(EVENT_LABELS[entry.eventKey] || entry.eventKey)
													: readOnly
													? t('Message from the administration')
													: t('Comment')}
											</span>
											<span className={styles.timelineMeta}>
												{formatVienna(entry.createdAt, 'dd.MM.yyyy HH:mm')}
												{entry.authorName && ` · ${entry.authorName}`}
											</span>
										</div>
										{entry.message && <p className={styles.timelineText}>{entry.message}</p>}
										{/* Ein Ereignis, das Schüler:innen und Lehrkräfte mitsehen (#230). */}
										{isEvent && shared && !readOnly && (
											<div className={styles.timelineTags}>
												{entry.audience.includes(AUDIENCE.STUDENTS) && (
													<span className={styles.timelineTag}>{t('employees')}</span>
												)}
												{entry.audience.includes(AUDIENCE.TEACHERS) && (
													<span className={styles.timelineTag}>{t('teachers')}</span>
												)}
											</div>
										)}
										{!isEvent && !readOnly && (
											<div className={styles.timelineTags}>
												{shared ? (
													<>
														{entry.audience.includes(AUDIENCE.STUDENTS) && (
															<span className={styles.timelineTag}>{t('employees')}</span>
														)}
														{entry.audience.includes(AUDIENCE.TEACHERS) && (
															<span className={styles.timelineTag}>{t('teachers')}</span>
														)}
														{entry.viaNotification && (
															<span className={styles.timelineTag}>
																{t('Internal message')}
															</span>
														)}
														{entry.viaEmail && (
															<span className={styles.timelineTag}>{t('Email')}</span>
														)}
													</>
												) : (
													<span
														className={clsx(styles.timelineTag, styles.timelineTagInternal)}
													>
														{t('Internal only')}
													</span>
												)}
											</div>
										)}
									</div>
								</li>
							)
						})}
					</ol>
				)}
			</section>
		</div>
	)
}

LogTab.defaultProps = {
	url: TApi.ADMIN_COMPANY_LOG
}

LogTab.propTypes = {
	companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	// Bekommt die Kennung der Junior Company und gibt die Adresse zurueck.
	url: PropTypes.func,
	readOnly: PropTypes.bool
}
